import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, ChevronDown, Home, Mail, Handshake, Info, HelpCircle, Tag, ArrowRight } from "lucide-react";

const FEATURED = [
  {
    to: "/try-on",
    title: "Virtual Try-On",
    objectPos: "center 22%",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/yru8ohic_thenewblack-aed61ab6-e9cb-44bd-98c8-ae75a47a4449.jpg",
  },
  {
    to: "/retouch",
    title: "Retouch",
    objectPos: "center 62%",
    img: "https://customer-assets-m6fa6gv7.emergentagent.net/job_vintage-you/artifacts/b2rkbk9u_thenewblack-156f3a02-871c-41a2-8cf5-0de6ebc741c2.jpg",
  },
];

const MORE = [
  { to: "/", label: "Home", Icon: Home },
  { to: "/contact", label: "Contact", Icon: Mail },
  { to: "/collab", label: "Collab", Icon: Handshake },
  { to: "/about", label: "About", Icon: Info },
  { to: "/faq", label: "FAQ", Icon: HelpCircle },
  { to: "/pricing", label: "Pricing", Icon: Tag },
];

export default function MegaMenu() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, []);

  const close = () => setOpen(false);

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="menu-trigger"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 sm:gap-2 text-sm px-2.5 sm:px-4 py-2 rounded-full border border-[#1A1A1A]/20 text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-[#FDFBF7] transition-colors duration-300"
      >
        <Menu className="w-4 h-4" strokeWidth={1.5} />
        <span className="hidden sm:inline uppercase tracking-[0.15em] text-xs">Menu</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </button>

      {open && (
        <div
          data-testid="menu-panel"
          className="absolute right-0 top-full mt-3 z-50 w-[calc(100vw-1.5rem)] md:w-[86vw] max-w-[860px] max-h-[80vh] overflow-y-auto md:max-h-none md:overflow-visible rounded-2xl border border-[#1A1A1A]/10 bg-[#FDFBF7]/95 backdrop-blur-xl shadow-[0_24px_60px_-20px_rgba(26,26,26,0.35)] p-5 md:p-8 text-left"
        >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Products */}
              <div className="md:col-span-2">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#8B5A2B] mb-4 md:mb-5">Explore</p>
                <div className="grid grid-cols-2 gap-4 md:gap-5">
                  {FEATURED.map((f) => (
                    <Link
                      key={f.to}
                      to={f.to}
                      onClick={close}
                      data-testid={`menu-featured-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group block"
                    >
                      <div className="aspect-[4/3] rounded-xl overflow-hidden bg-[#F4F1EA] mb-2.5 md:mb-3">
                        <img src={f.img} alt={f.title} loading="lazy" style={{ objectPosition: f.objectPos || "top" }} className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-[800ms]" />
                      </div>
                      <div className="flex items-center justify-between">
                        <h4 className="font-serif-display text-lg md:text-xl text-[#1A1A1A]">{f.title}</h4>
                        <ArrowRight className="w-4 h-4 shrink-0 text-[#6D214F] group-hover:translate-x-1 transition-transform duration-300" strokeWidth={1.5} />
                      </div>
                    </Link>
                  ))}
                </div>
              </div>

              {/* More */}
              <div className="border-t border-[#1A1A1A]/10 pt-6 md:border-t-0 md:pt-0 md:border-l md:border-[#1A1A1A]/10 md:pl-8">
                <p className="text-[10px] uppercase tracking-[0.35em] text-[#8B5A2B] mb-4 md:mb-5">More</p>
                <nav className="grid grid-cols-2 gap-1 md:flex md:flex-col">
                  {MORE.map((m) => (
                    <Link
                      key={m.to}
                      to={m.to}
                      onClick={close}
                      data-testid={`menu-link-${m.label.toLowerCase()}`}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#1A1A1A] hover:bg-[#F4F1EA] transition-colors duration-200"
                    >
                      <m.Icon className="w-4 h-4 text-[#8B5A2B]" strokeWidth={1.5} />
                      {m.label}
                    </Link>
                  ))}
                </nav>

                <Link
                  to="/try-on"
                  onClick={close}
                  data-testid="menu-waitlist-tile"
                  className="mt-5 flex items-center gap-3 rounded-xl border border-[#6D214F]/25 bg-[#6D214F]/5 px-4 py-3.5 hover:bg-[#6D214F]/10 transition-colors duration-200"
                >
                  <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#6D214F] text-[#FDFBF7]">
                    <Mail className="w-4 h-4" strokeWidth={1.5} />
                  </span>
                  <span>
                    <span className="block text-sm font-medium text-[#1A1A1A]">Join the Waitlist</span>
                    <span className="block text-xs text-[#4A4A4A]">Be first when we launch</span>
                  </span>
                </Link>
              </div>
            </div>
          </div>
      )}
    </div>
  );
}
